import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Download, Share, MoreVertical, CheckCircle2, Smartphone, ArrowLeft } from 'lucide-react';
import { getInstallPrompt, onInstallPromptChange, triggerInstall, isStandalone, isIOS } from '../lib/installPrompt';

const APPS = {
  tech: { key: 'tech', name: 'AQH Tech', icon: '/icon-192.png', color: 'from-brand-blue to-brand-teal', who: 'Technicians and crew', openUrl: '/employee/dashboard?source=pwa',
    bullets: ['Today\'s jobs, addresses and tap-to-call', 'Clock in/out, GPS mileage and photos — even with no signal', 'On My Way / Running Late texts to the customer', 'Push alerts for new assignments'] },
  admin: { key: 'admin', name: 'AQH Admin', icon: '/icon-admin-192.png', color: 'from-slate-800 to-teal-700', who: 'Owner and office staff', openUrl: '/aqh-mgmt-7816?source=pwa',
    bullets: ['Calendar, bookings and customer profiles', 'Estimates, invoices and payments', 'SMS chat and reviews', 'Push alerts for new bookings, replies and payments'] },
};

const Steps = ({ ios }) => ios ? (
  <ol className="text-sm text-slate-700 space-y-2 list-decimal pl-5" data-testid="install-steps-ios">
    <li>Open this page in <strong>Safari</strong> (other browsers can't install on iPhone).</li>
    <li>Tap the <Share className="inline w-4 h-4 -mt-0.5" /> <strong>Share</strong> button at the bottom of the screen.</li>
    <li>Scroll and tap <strong>Add to Home Screen</strong>, then <strong>Add</strong>.</li>
  </ol>
) : (
  <ol className="text-sm text-slate-700 space-y-2 list-decimal pl-5" data-testid="install-steps-android">
    <li>Open this page in <strong>Chrome</strong>.</li>
    <li>Tap the <MoreVertical className="inline w-4 h-4 -mt-0.5" /> menu (top right).</li>
    <li>Tap <strong>Install app</strong> or <strong>Add to Home screen</strong>, then confirm.</li>
  </ol>
);

export default function GetAppPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const which = params.get('app') === 'admin' ? 'admin' : 'tech';
  const app = APPS[which];
  const [prompt, setPrompt] = useState(getInstallPrompt());
  const [done, setDone] = useState(false);
  useEffect(() => onInstallPromptChange(setPrompt), []);
  const ios = isIOS();
  const installed = isStandalone();

  return (
    <div className="min-h-screen bg-slate-50" data-testid="get-app-page">
      <div className="max-w-3xl mx-auto px-4 py-8 md:py-14">
        <button onClick={() => navigate(-1)} className="text-sm text-slate-500 hover:text-slate-800 inline-flex items-center gap-1 mb-6" data-testid="get-app-back"><ArrowLeft className="w-4 h-4" />Back</button>
        <div className="flex items-center gap-3 mb-2"><Smartphone className="w-7 h-7 text-teal-700" /><h1 className="text-3xl md:text-4xl font-bold text-slate-900">Get the A Quality Handyman apps</h1></div>
        <p className="text-slate-600 mb-8">Free, no app store needed. Installs from this page and opens full-screen like any other app on your phone. Pick the app you need:</p>

        <div className="inline-flex rounded-full bg-white border border-slate-200 p-1 mb-8 shadow-sm" data-testid="get-app-switch">
          {Object.values(APPS).map((a) => (
            <button key={a.key} onClick={() => { setParams({ app: a.key }); if ((a.key === 'admin') !== (document.querySelector('link[rel="manifest"]')?.getAttribute('href') === '/manifest-admin.json')) window.location.href = `/app?app=${a.key}`; }}
              className={`px-5 py-2 rounded-full text-sm font-semibold transition-colors ${which === a.key ? 'bg-teal-700 text-white' : 'text-slate-600 hover:text-slate-900'}`} data-testid={`get-app-tab-${a.key}`}>{a.name}</button>
          ))}
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden" data-testid={`get-app-card-${which}`}>
          <div className={`bg-gradient-to-r ${app.color} text-white p-6 flex items-center gap-4`}>
            <img src={app.icon} alt="" className="w-16 h-16 rounded-2xl shadow-lg bg-white/10" />
            <div><h2 className="text-xl font-bold">{app.name}</h2><p className="text-white/80 text-sm">{app.who}</p></div>
          </div>
          <div className="p-6 grid md:grid-cols-2 gap-6">
            <div>
              <h3 className="font-semibold text-slate-900 mb-2">What you get</h3>
              <ul className="space-y-1.5">{app.bullets.map((b) => <li key={b} className="text-sm text-slate-700 flex gap-2"><CheckCircle2 className="w-4 h-4 text-teal-600 mt-0.5 shrink-0" />{b}</li>)}</ul>
            </div>
            <div>
              {installed || done ? (
                <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-4 text-sm text-emerald-800" data-testid="get-app-installed">
                  <p className="font-semibold">{done ? 'Installed — look for the icon on your home screen.' : 'You are already using the installed app.'}</p>
                  <Button className="mt-3 w-full" variant="outline" onClick={() => navigate(app.openUrl)} data-testid="get-app-open">Open {app.name}</Button>
                </div>
              ) : prompt ? (
                <>
                  <Button className="w-full h-12 text-base bg-teal-700 hover:bg-teal-800" onClick={async () => { if ((await triggerInstall()) === 'accepted') setDone(true); }} data-testid="get-app-install-btn"><Download className="w-5 h-5 mr-2" />Install {app.name}</Button>
                  <p className="text-xs text-slate-500 mt-2">Your phone will ask you to confirm. Takes about two seconds.</p>
                </>
              ) : (
                <>
                  <h3 className="font-semibold text-slate-900 mb-2">How to install</h3>
                  <Steps ios={ios} />
                  {!ios && <p className="text-xs text-slate-500 mt-3">If you see an <strong>Install</strong> option in the address bar or menu, that works too.</p>}
                </>
              )}
              <p className="text-xs text-slate-500 mt-4">Signed in already? <button className="underline" onClick={() => navigate(app.openUrl)} data-testid="get-app-open-web">Open {app.name} in the browser</button></p>
            </div>
          </div>
        </div>

        <p className="text-xs text-slate-500 mt-8">Tip: install the Tech app from <strong>/app</strong> and the Admin app from <strong>/app?app=admin</strong> — each gets its own icon and name on your home screen.</p>
      </div>
    </div>
  );
}
